import { forwardRef, useRef } from 'react';
import type { TextInput } from 'react-native';
import { View } from 'react-native';

import { Input, Text } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { withBottomSheetInput } from '@/utils/withBottomSheetInput';

import { styles } from './styles';
import type { OtpInputProps } from './types';

const OtpWithBottomSheetInput = withBottomSheetInput(Input);

const OtpInput = forwardRef<View, OtpInputProps>(
  (
    {
      length = 6,
      value,
      onChangeText,
      onComplete,
      disabled = false,
      error = false,
      success = false,
      errorMessage,
      charValidationRegex = /^\d*$/,
      accessibilityLabel,
      accessibilityHint,
      testID,
      containerStyle,
      keyboardType = 'number-pad',
      insideBottomSheet = false,
      style,
      ...props
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('shared');
    const inputRefs = useRef<(TextInput | null)[]>([]);

    // Select the appropriate input component based on context
    const InputComponent = insideBottomSheet ? OtpWithBottomSheetInput : Input;

    const defaultAccessibilityLabel =
      accessibilityLabel ?? t('otpInput.accessibility.label');
    const defaultAccessibilityHint =
      accessibilityHint ?? t('otpInput.accessibility.hint');

    const focusInputAtIndex = (index: number) => {
      inputRefs.current[Math.min(index, length - 1)]?.focus();
    };

    const handleChangeCharacter = (index: number, char: string) => {
      const chars = [...value];
      chars[index] = char?.[0] || '';

      return chars;
    };

    const handleChangeText = (text: string, index: number) => {
      if (!charValidationRegex.test(text) || !text) return;

      let newValue = [...value];
      if (text.length === 1) {
        newValue = handleChangeCharacter(index, text[0] || '');
      } else {
        newValue = text.split('');

        newValue.forEach((char, i) => {
          newValue[i] = char;
        });
      }

      onChangeText(newValue);
      focusInputAtIndex(index + 1);

      if (newValue.every((digit) => digit !== '')) {
        onComplete?.(newValue.join(''));
      }
    };

    const handleKeyPress = (key: string, index: number) => {
      if (key.toLowerCase() === 'backspace') {
        onChangeText(handleChangeCharacter(index, ''));
        focusInputAtIndex(index - 1);
      }
    };

    const getInputAccessibilityLabel = (index: number) => {
      return `${defaultAccessibilityLabel} digit ${index + 1}`;
    };

    const getInputAccessibilityHint = (index: number) => {
      return `Enter digit ${index + 1} of ${length} for verification`;
    };

    return (
      <View
        ref={ref}
        testID={testID}
        accessibilityLabel={defaultAccessibilityLabel}
        accessibilityHint={defaultAccessibilityHint}
        style={containerStyle}
        {...props}
      >
        <View style={[styles.container, style]}>
          {Array.from({ length }, (_, index) => (
            <InputComponent
              maxLength={index > 0 ? 1 : undefined}
              key={index}
              ref={(r) => {
                inputRefs.current[index] = r;
              }}
              error={error}
              success={success}
              style={styles.input}
              containerStyle={styles.inputContainer}
              value={value[index] || ''}
              onChangeText={(text) => handleChangeText(text, index)}
              onKeyPress={({ nativeEvent }) =>
                handleKeyPress(nativeEvent.key, index)
              }
              onFocus={() => focusInputAtIndex(index)}
              keyboardType={keyboardType}
              autoCapitalize="none"
              autoComplete={index === 0 ? 'one-time-code' : undefined}
              textContentType="oneTimeCode"
              autoCorrect={false}
              selectTextOnFocus
              autoFocus={index === 0}
              editable={!disabled}
              accessibilityRole="text"
              accessibilityLabel={getInputAccessibilityLabel(index)}
              accessibilityHint={getInputAccessibilityHint(index)}
              testID={testID ? `${testID}-input-${index}` : undefined}
            />
          ))}
        </View>

        {error && errorMessage && (
          <Text
            style={styles.errorText}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
            testID={testID ? `${testID}-error` : undefined}
          >
            {errorMessage}
          </Text>
        )}
      </View>
    );
  },
);

OtpInput.displayName = 'OtpInput';
export { OtpInput };
